import React from "react";
import { X, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useI18n } from "../contexts/I18nContext";
import { Link } from "react-router-dom";
import { describeConfiguration } from "../lib/orderSummary";
import VolumeDiscountRow from "./VolumeDiscountRow";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQty, totalItems } = useCart();
  const { t, lang } = useI18n();

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsOpen(false)}
        data-testid="cart-overlay"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#080808] border-l border-white/10 z-[61] transform transition-transform duration-400 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <ShoppingBag size={20} className="text-[#39FF14]" />
              <h3 className="font-display text-xl font-bold uppercase tracking-wider">
                {t.cart.title}
              </h3>
              <span className="font-mono text-xs text-zinc-500">
                {totalItems} {t.cart.total_items}
              </span>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-zinc-400 hover:text-[#39FF14] transition-colors"
              data-testid="cart-close-button"
              aria-label={t.common.close}
            >
              <X size={20} />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-4">
                <ShoppingBag size={40} className="text-zinc-700" />
                <p className="text-zinc-400 font-body">{t.cart.empty}</p>
                <Link
                  to="/produits"
                  onClick={() => setIsOpen(false)}
                  className="btn-ghost-neon text-xs mt-2"
                  data-testid="cart-empty-continue"
                >
                  {t.cart.continue}
                </Link>
              </div>
            ) : (
              <ul className="space-y-4">
                {items.map((item) => (
                  <li
                    key={item.lineId}
                    className="flex gap-4 bg-[#0f0f0f] border border-white/5 p-3"
                    data-testid={`cart-item-${item.lineId}`}
                  >
                    {(() => {
                      const previewImg = item.configuration?.previewImage;
                      const alt = (typeof item.product_name === "object" ? (item.product_name[lang] || item.product_name.fr) : item.product_name) || "";
                      return previewImg ? (
                        <img
                          src={previewImg}
                          alt={alt}
                          className="w-20 h-20 object-contain bg-black border border-[#39FF14]/30"
                          data-testid={`cart-drawer-preview-${item.lineId}`}
                        />
                      ) : (
                        <img
                          src={item.image}
                          alt={alt}
                          className="w-20 h-20 object-cover"
                        />
                      );
                    })()}
                    <div className="flex-1 flex flex-col min-w-0">
                      <div className="font-display font-semibold text-sm text-white leading-tight">
                        {typeof item.product_name === "object" ? (item.product_name[lang] || item.product_name.fr) : item.product_name}
                      </div>
                      {item.configuration && (() => {
                        const desc = describeConfiguration(item.product_slug, item.configuration, lang).join(" · ");
                        return desc ? (
                          <div className="mt-1 text-xs text-zinc-500 font-mono truncate" data-testid={`cart-drawer-desc-${item.lineId}`}>
                            {desc}
                          </div>
                        ) : null;
                      })()}
                      <div className="mt-auto flex items-center gap-3">
                        <div className="flex items-center border border-white/10">
                          <button
                            onClick={() => updateQty(item.lineId, item.quantity - 1)}
                            className="w-7 h-7 text-zinc-400 hover:text-[#39FF14]"
                            data-testid={`cart-qty-decrement-${item.lineId}`}
                          >
                            −
                          </button>
                          <span className="w-7 text-center font-mono text-xs">{item.quantity}</span>
                          <button
                            onClick={() => updateQty(item.lineId, item.quantity + 1)}
                            className="w-7 h-7 text-zinc-400 hover:text-[#39FF14]"
                            data-testid={`cart-qty-increment-${item.lineId}`}
                          >
                            +
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.lineId)}
                          className="ml-auto text-zinc-500 hover:text-red-400 transition-colors"
                          data-testid={`cart-remove-${item.lineId}`}
                          aria-label={t.cart.remove}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-white/10 px-6 py-5 space-y-4">
              {(() => {
                const subtotal = items.reduce(
                  (s, it) => s + (Number(it.unit_price_chf) || 0) * (it.quantity || 1),
                  0,
                );
                if (subtotal <= 0) return null;
                return <VolumeDiscountRow subtotal={subtotal} lang={lang} compact />;
              })()}
              <p className="text-xs text-zinc-500 font-body leading-relaxed">
                {t.cart.note}
              </p>
              <Link
                to="/checkout"
                onClick={() => setIsOpen(false)}
                className="btn-neon w-full justify-center"
                data-testid="cart-checkout-button"
              >
                {t.cart.checkout}
              </Link>
              <Link
                to="/produits"
                onClick={() => setIsOpen(false)}
                className="block text-center font-mono text-xs uppercase tracking-widest text-zinc-400 hover:text-[#39FF14] transition-colors"
                data-testid="cart-continue-shopping"
              >
                {t.cart.continue}
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
